import React, { useState, useRef, useEffect } from 'react';
import './Image.css';

const OptimizedImage = ({
    src,
    alt,
    className,
    width,
    height,
    priority = false,
    onLoad,
    ...props
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isInView, setIsInView] = useState(false);
    const imgRef = useRef(null);

    useEffect(() => {
        if (!priority) {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    setIsInView(entry.isIntersecting);
                },
                {
                    rootMargin: '50px'
                }
            );

            if (imgRef.current) {
                observer.observe(imgRef.current);
            }

            return () => {
                if (imgRef.current) {
                    observer.unobserve(imgRef.current);
                }
            };
        } else {
            setIsInView(true);
        }
    }, [priority]);

    const handleLoad = (e) => {
        setIsLoaded(true);
        if (onLoad) {
            onLoad(e);
        }
    };

    // Generate srcSet for responsive images
    const generateSrcSet = () => {
        const widths = [320, 640, 960, 1280, 1920];
        const imageUrl = new URL(src, window.location.origin);

        return widths
            .map(w => {
                imageUrl.searchParams.set('width', w.toString());
                return `${imageUrl.toString()} ${w}w`;
            })
            .join(', ');
    };

    return (
        <div
            className={`optimized-image-container ${className || ''}`}
            style={{ width, height }}
            ref={imgRef}
        >
            {(isInView || priority) && (
                <img
                    src={src}
                    alt={alt}
                    className={`optimized-image ${isLoaded ? 'loaded' : ''}`}
                    onLoad={handleLoad}
                    loading={priority ? 'eager' : 'lazy'}
                    srcSet={generateSrcSet()}
                    sizes="(max-width: 320px) 280px,
                (max-width: 640px) 580px,
                (max-width: 960px) 780px,
                1140px"
                    {...props}
                />
            )}
            {!isLoaded && (isInView || priority) && (
                <div className="image-placeholder" />
            )}
        </div>
    );
};

export default OptimizedImage;
